import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import type { UserRole } from '@produzionejb/types';
import { useAuthStore } from '../../stores/auth-store';
import {
  useUsers,
  useInviteUser,
  useUpdateUser,
  useResendInvite,
  useUnlockUser,
  useDeleteUser,
  type InviteCreatedResponse,
} from '../../lib/users-api';

const ROLE_OPTIONS: UserRole[] = ['ADMIN', 'MANAGER', 'OPERATOR'];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function StatusBadge({ children, tone }: { children: string; tone: 'green' | 'amber' | 'red' | 'slate' }) {
  const cls = {
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
    slate: 'bg-slate-100 text-slate-700',
  }[tone];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

export function UsersListPage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const { data, isLoading, error } = useUsers();
  const updateMut = useUpdateUser();
  const resendMut = useResendInvite();
  const unlockMut = useUnlockUser();
  const deleteMut = useDeleteUser();

  const [showInvite, setShowInvite] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState<{ url: string; email: string } | null>(null);

  function handleInviteCreated(res: InviteCreatedResponse) {
    setShowInvite(false);
    if (res.inviteUrl) {
      setLastInviteUrl({ url: res.inviteUrl, email: res.user.email });
    }
  }

  async function handleResend(id: string, email: string) {
    try {
      const res = await resendMut.mutateAsync(id);
      if (res.inviteUrl) setLastInviteUrl({ url: res.inviteUrl, email });
      else window.alert(t('users.toast.resendOk', { email }));
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function handleToggleActive(id: string, current: boolean) {
    try {
      await updateMut.mutateAsync({ id, body: { active: !current } });
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function handleChangeRole(id: string, role: UserRole) {
    try {
      await updateMut.mutateAsync({ id, body: { role } });
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function handleUnlock(id: string) {
    try {
      await unlockMut.mutateAsync(id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function handleDelete(id: string, email: string) {
    if (!window.confirm(t('users.confirmDelete', { email }))) return;
    try {
      await deleteMut.mutateAsync(id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{t('users.list.title')}</h2>
        <Button onClick={() => setShowInvite(true)}>{t('users.list.inviteButton')}</Button>
      </div>

      {data && !data.smtpConfigured && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t('users.list.smtpNotConfigured')}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('users.list.tableTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <p className="px-4 py-3 text-sm text-slate-500">{t('common.loading')}</p>}
          {error && <p className="px-4 py-3 text-sm text-red-700">{(error as Error).message}</p>}
          {data && data.items.length === 0 && (
            <p className="px-4 py-6 text-sm text-slate-500">{t('users.list.empty')}</p>
          )}
          {data && data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3">{t('users.fields.email')}</th>
                    <th className="px-4 py-3">{t('users.fields.name')}</th>
                    <th className="px-4 py-3">{t('users.fields.role')}</th>
                    <th className="px-4 py-3">{t('users.fields.status')}</th>
                    <th className="px-4 py-3">{t('users.fields.mfa')}</th>
                    <th className="px-4 py-3">{t('users.fields.lastLogin')}</th>
                    <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((u) => {
                    const isSelf = me?.id === u.id;
                    const isLocked = u.lockedUntil && new Date(u.lockedUntil) > new Date();
                    const isPending = !u.activatedAt;
                    return (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {u.email}
                          {isSelf && <span className="ml-2 text-xs text-slate-400">{t('users.fields.you')}</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.name ?? '—'}</td>
                        <td className="px-4 py-3">
                          <select
                            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
                            value={u.role}
                            disabled={isSelf || updateMut.isPending}
                            onChange={(e) => void handleChangeRole(u.id, e.target.value as UserRole)}
                          >
                            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          {!u.active && <StatusBadge tone="slate">{t('users.status.disabled')}</StatusBadge>}
                          {u.active && isPending && <StatusBadge tone="amber">{t('users.status.pending')}</StatusBadge>}
                          {u.active && !isPending && !isLocked && <StatusBadge tone="green">{t('users.status.active')}</StatusBadge>}
                          {u.active && isLocked && <StatusBadge tone="red">{t('users.status.locked')}</StatusBadge>}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.mfaEnabled ? '✓' : '—'}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(u.lastLoginAt)}</td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          {isPending && (
                            <button
                              type="button"
                              onClick={() => void handleResend(u.id, u.email)}
                              className="text-xs font-medium text-brand-700 hover:underline"
                              disabled={resendMut.isPending}
                            >
                              {t('users.actions.resendInvite')}
                            </button>
                          )}
                          {isLocked && (
                            <button
                              type="button"
                              onClick={() => void handleUnlock(u.id)}
                              className="text-xs font-medium text-brand-700 hover:underline"
                              disabled={unlockMut.isPending}
                            >
                              {t('users.actions.unlock')}
                            </button>
                          )}
                          {!isSelf && (
                            <button
                              type="button"
                              onClick={() => void handleToggleActive(u.id, u.active)}
                              className="text-xs font-medium text-slate-600 hover:underline"
                              disabled={updateMut.isPending}
                            >
                              {u.active ? t('users.actions.disable') : t('users.actions.enable')}
                            </button>
                          )}
                          {!isSelf && (
                            <button
                              type="button"
                              onClick={() => void handleDelete(u.id, u.email)}
                              className="text-xs font-medium text-red-600 hover:underline"
                              disabled={deleteMut.isPending}
                            >
                              {t('common.delete')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showInvite && (
        <InviteDialog onClose={() => setShowInvite(false)} onCreated={handleInviteCreated} />
      )}

      {lastInviteUrl && (
        <InviteLinkDialog
          email={lastInviteUrl.email}
          url={lastInviteUrl.url}
          onClose={() => setLastInviteUrl(null)}
        />
      )}
    </div>
  );
}

function InviteDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (res: InviteCreatedResponse) => void;
}) {
  const { t } = useTranslation();
  const inviteMut = useInviteUser();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('OPERATOR');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      const res = await inviteMut.mutateAsync({
        email: email.trim(),
        role,
        ...(name.trim() && { name: name.trim() }),
      });
      onCreated(res);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 p-6">
          <h3 className="text-lg font-semibold text-slate-900">{t('users.invite.title')}</h3>
          <p className="text-sm text-slate-600">{t('users.invite.description')}</p>
          <div className="space-y-1">
            <Label>{t('users.fields.email')} *</Label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@esempio.com"
            />
          </div>
          <div className="space-y-1">
            <Label>{t('users.fields.name')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome Cognome" />
          </div>
          <div className="space-y-1">
            <Label>{t('users.fields.role')} *</Label>
            <select
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
            >
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={inviteMut.isPending}>
              {inviteMut.isPending ? t('common.loading') : t('users.invite.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteLinkDialog({
  email,
  url,
  onClose,
}: {
  email: string;
  url: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt(t('users.invite.copyManual'), url);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="space-y-3 p-6">
          <h3 className="text-lg font-semibold text-slate-900">{t('users.inviteLink.title')}</h3>
          <p className="text-sm text-slate-600">{t('users.inviteLink.description', { email })}</p>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs break-all font-mono">
            {url}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={handleCopy}>
              {copied ? t('users.inviteLink.copied') : t('users.inviteLink.copy')}
            </Button>
            <Button type="button" onClick={onClose}>{t('common.close', { defaultValue: 'Chiudi' })}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
